import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { useOpenBriefCreationDrawer } from '@fiverr-private/dm_brief_creation_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { ChevronRightIcon, ChevronLeftIcon } from '@fiverr-private/visuals';
import { trackBriefCtaClick } from '../../services/report';
import BriefButton from '../BriefButton';

import './index.scss';

const SCROLL_STEP = 350;
const CHECK_OVERFLOW_DELAY = 50;
const POINTER_EVENTS_VALUES = {
    none: 'none',
    auto: 'auto',
};

const Scroller = ({ mode = 'default', children, classname, currency, shouldShowPostBriefCta, ctaSource }) => {
    const [isMounted, setIsMounted] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const ref = useRef();
    const navRef = useRef();
    const rightBtnRef = useRef();
    const leftBtnRef = useRef();
    const { openBriefCreationDrawer } = useOpenBriefCreationDrawer();

    const { url, pageCtxId, organizationId } = getContext();

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const pageName = new URL(url);
    const pathname = pageName.pathname;

    const onBriefCLick = () => {
        trackBriefCtaClick({ pathname, pageCtxId, organizationId, ctaSource });
        openBriefCreationDrawer({ currency, source: ctaSource });
    };

    const checkOverflow = () => {
        requestAnimationFrame(() => {
            const scrollLeft = Math.ceil(ref.current.scrollLeft),
                scrollView = ref.current.scrollWidth - ref.current.clientWidth,
                canScrollLeft = scrollLeft > 0,
                canScrollRight = scrollLeft < scrollView;

            setCanScrollLeft(canScrollLeft);
            setCanScrollRight(canScrollRight);
        });
    };

    const disableLimitItems = useCallback(() => {
        const listItems = document.querySelectorAll('li[data-level=top]');
        const navLeft = navRef.current?.getBoundingClientRect().left;
        const navRight = navRef.current?.getBoundingClientRect().right;
        const leftBtnWidth = leftBtnRef.current?.getBoundingClientRect().width || 0;
        const rightBtnWidth = rightBtnRef.current?.getBoundingClientRect().width || 0;

        listItems.forEach((item) => {
            item.style.pointerEvents = POINTER_EVENTS_VALUES.auto;
        });

        if (canScrollRight) {
            listItems.forEach((item) => {
                const itemRight = item.getBoundingClientRect().right;
                const distanceToRightEdge = navRight - itemRight;

                if (distanceToRightEdge <= rightBtnWidth) {
                    item.style.pointerEvents = POINTER_EVENTS_VALUES.none;
                }
            });
        }

        if (canScrollLeft) {
            listItems.forEach((item) => {
                const itemLeft = item.getBoundingClientRect().left;
                const distanceToLeftEdge = itemLeft - navLeft;

                if (distanceToLeftEdge <= leftBtnWidth) {
                    item.style.pointerEvents = POINTER_EVENTS_VALUES.none;
                }
            });
        }
    }, [canScrollRight, canScrollLeft]);

    useEffect(() => {
        disableLimitItems();
    }, [canScrollLeft, canScrollRight, disableLimitItems]);

    const checkOverflowDebounced = debounce(checkOverflow, CHECK_OVERFLOW_DELAY);

    useEffect(() => {
        const scroller = ref.current;

        const handleScroll = () => {
            checkOverflowDebounced();
            disableLimitItems();
        };
        scroller?.addEventListener('scroll', handleScroll);

        const handleResize = () => {
            checkOverflowDebounced();
            disableLimitItems();
        };
        window.addEventListener('resize', handleResize);

        checkOverflow();

        return () => {
            scroller?.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [ref, checkOverflowDebounced, disableLimitItems]);

    const scrollLeft = () => {
        scroll(-SCROLL_STEP);
    };

    const scrollRight = () => {
        scroll(SCROLL_STEP);
    };

    const scroll = (delta) => {
        ref.current.scrollTo({
            left: ref.current.scrollLeft + delta,
            behavior: 'smooth',
        });
    };

    const className = classnames('categories-menu-package', classname, {
        pro: mode === 'pro',
        'has-overflow has-overflow-vertical': canScrollLeft || canScrollRight,
    });

    return (
        <Stack
            paddingY={shouldShowPostBriefCta ? '3' : '0'}
            gap="5"
            className="categories-padding-wrapper"
            maxHeight="60px"
            maxWidth="1400px"
            marginX="auto"
        >
            {shouldShowPostBriefCta && (
                <Stack gap="5" backgroundColor="white" zIndex="1">
                    <BriefButton onBriefClick={onBriefCLick} />
                    <Divider direction="vertical" />
                </Stack>
            )}
            <nav id="categories-menu-package" ref={navRef} className={className}>
                {canScrollLeft && (
                    <button ref={leftBtnRef} className="left" onClick={scrollLeft}>
                        <ChevronLeftIcon />
                    </button>
                )}
                {(isMounted ? canScrollRight : true) && (
                    <button ref={rightBtnRef} className="right" onClick={scrollRight}>
                        <ChevronRightIcon />
                    </button>
                )}
                <ul className="categories" ref={ref}>
                    {children}
                </ul>
            </nav>
        </Stack>
    );
};

Scroller.propTypes = {
    children: PropTypes.node,
    mode: PropTypes.string,
    classname: PropTypes.string,
    currency: PropTypes.object,
    shouldShowPostBriefCta: PropTypes.bool,
    ctaSource: PropTypes.string,
};

export default Scroller;
